<template>
  <PreviewFrame :accent="slide.accent">
    <template #presenter>
      <div class="dr-question">
        <span class="dr-bar dr-bar--xl"></span>
      </div>
      <div class="dr-track">
        <div
          v-for="(d, i) in ducks"
          :key="d.id"
          class="dr-lane"
          :class="{ '--leader': i === 0 }"
          :style="{ '--lane-i': i }"
        >
          <span class="dr-lane-label">{{ d.id }}</span>
          <span class="dr-duck">
            <span class="material-symbols-rounded">cruelty_free</span>
          </span>
          <span class="dr-finish"></span>
        </div>
      </div>
      <div class="dr-leader-strip">
        <span class="dr-leader-cap">LEADING</span>
        <span class="dr-leader-name">Duck #1</span>
      </div>
    </template>

    <template #audience>
      <span class="dr-phone-q"></span>
      <div class="dr-phone-screen">
        <span class="material-symbols-rounded dr-phone-duck">cruelty_free</span>
        <span class="dr-phone-text">Cheer your duck!</span>
      </div>
      <button type="button" class="dr-phone-cheer" tabindex="-1">
        <span class="material-symbols-rounded">campaign</span>
        Cheer!
      </button>
    </template>
  </PreviewFrame>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue'
import type { SlideType } from '../../data/slideTypeCatalog'
import PreviewFrame from './PreviewFrame.vue'

export default Vue.extend({
  name: 'PreviewDuckRace',
  components: { PreviewFrame },
  props: {
    slide: { type: Object as PropType<SlideType>, required: true },
  },
  data() {
    return {
      ducks: [{ id: 1 }, { id: 2 }, { id: 3 }],
    }
  },
})
</script>

<style scoped lang="scss">
.dr-bar {
  display: block; height: 8px; border-radius: 999px;
  background: rgba(15, 15, 20, 0.18);
}
.dr-bar--xl { width: 70%; }

.dr-question {
  padding-bottom: 8px;
  border-bottom: 1px dashed color-mix(in srgb, var(--accent) 20%, transparent);
}

.dr-track {
  display: grid; gap: 6px;
  flex: 1;
}
.dr-lane {
  position: relative;
  display: grid;
  grid-template-columns: 22px 1fr auto;
  align-items: center;
  gap: 8px;
  padding: 6px 0;
  border-bottom: 2px dashed rgba(15, 15, 20, 0.06);
}
.dr-lane-label {
  font-size: 10px; font-weight: 800;
  color: rgba(15, 15, 20, 0.5);
}
.dr-duck {
  position: relative;
  height: 22px;
  display: flex; align-items: center;
  animation: dr-paddle 4s ease-in-out infinite;
  animation-delay: calc(var(--lane-i, 0) * 80ms);
}
.dr-duck .material-symbols-rounded {
  font-size: 22px;
  color: var(--accent);
  filter: brightness(1);
}
.dr-lane:nth-child(1) .dr-duck .material-symbols-rounded { transform: translateX(0); animation: dr-go-1 4s ease-out infinite; }
.dr-lane:nth-child(2) .dr-duck .material-symbols-rounded { transform: translateX(0); animation: dr-go-2 4s ease-out infinite; }
.dr-lane:nth-child(3) .dr-duck .material-symbols-rounded { transform: translateX(0); animation: dr-go-3 4s ease-out infinite; }

.dr-lane.--leader .dr-duck .material-symbols-rounded {
  color: var(--accent);
  filter: drop-shadow(0 0 4px color-mix(in srgb, var(--accent) 50%, transparent));
}
.dr-finish {
  width: 4px; height: 26px;
  background: repeating-linear-gradient(
    to bottom,
    rgba(15, 15, 20, 0.85) 0 4px,
    white 4px 8px
  );
  border-radius: 2px;
}

.dr-leader-strip {
  display: flex; align-items: baseline; gap: 8px;
  padding-top: 4px;
}
.dr-leader-cap {
  font-size: 9px; font-weight: 700;
  letter-spacing: 0.08em; text-transform: uppercase;
  color: rgba(15, 15, 20, 0.45);
}
.dr-leader-name {
  font-size: 13px; font-weight: 800;
  color: var(--accent);
}

/* ---- Audience phone ---- */
.dr-phone-q {
  display: block; height: 8px; border-radius: 999px;
  width: 70%; background: rgba(15, 15, 20, 0.18);
  margin-bottom: 4px;
}
.dr-phone-screen {
  background: color-mix(in srgb, var(--accent) 8%, white);
  border-radius: 10px;
  padding: 14px 8px;
  display: flex; flex-direction: column; align-items: center; gap: 6px;
}
.dr-phone-duck {
  font-size: 32px; color: var(--accent);
}
.dr-phone-text {
  font-size: 11px; font-weight: 700;
  color: rgba(15, 15, 20, 0.7);
}
.dr-phone-cheer {
  appearance: none; border: none;
  background: var(--accent); color: white;
  font-family: inherit; font-weight: 800; font-size: 12px;
  padding: 10px; border-radius: 8px;
  display: flex; align-items: center; justify-content: center; gap: 4px;
  cursor: pointer;
  animation: dr-tap 4s ease-out infinite;
}
.dr-phone-cheer .material-symbols-rounded { font-size: 14px; }

@keyframes dr-go-1 {
  0%   { transform: translateX(0); }
  85%  { transform: translateX(80px); }
  100% { transform: translateX(80px); }
}
@keyframes dr-go-2 {
  0%   { transform: translateX(0); }
  85%  { transform: translateX(60px); }
  100% { transform: translateX(60px); }
}
@keyframes dr-go-3 {
  0%   { transform: translateX(0); }
  85%  { transform: translateX(45px); }
  100% { transform: translateX(45px); }
}
@keyframes dr-paddle {
  0%, 100% { transform: translateY(0); }
  50%      { transform: translateY(-2px); }
}
@keyframes dr-tap {
  0%, 35%  { transform: scale(1); }
  45%      { transform: scale(0.95); }
  55%, 100% { transform: scale(1); }
}

@media (prefers-reduced-motion: reduce) {
  .dr-duck, .dr-phone-cheer { animation: none; }
  .dr-lane:nth-child(1) .dr-duck .material-symbols-rounded { transform: translateX(60px); animation: none; }
  .dr-lane:nth-child(2) .dr-duck .material-symbols-rounded { transform: translateX(45px); animation: none; }
  .dr-lane:nth-child(3) .dr-duck .material-symbols-rounded { transform: translateX(30px); animation: none; }
}
</style>
